import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, User, AtSign, CheckSquare } from 'lucide-react';

const LoginPage: React.FC = () => {
    const { t } = useTranslation('common');
    const { user, accountId, login, refresh } = useAuth();
    const navigate = useNavigate();

    // SIGN-UP vs LOG-IN intent. The only behavioural difference: a SIGNUP on an email that already
    // has an account is shown "account exists — log in or create separate" instead of being silently
    // logged in. Mode comes from ?intent=signup (so a "Sign up" link elsewhere lands here in signup
    // mode) and is toggleable. It is carried through Google via the OAuth state param, and applied to
    // the email flow by inspecting verify-code's `next`.
    const initialMode = new URLSearchParams(useLocation().search).get('intent') === 'signup' ? 'signup' : 'login';
    const [mode, setMode] = useState<'login' | 'signup'>(initialMode);
    const isSignup = mode === 'signup';

    // Passwordless email sign-in. Two steps: ask for a code, then verify it.
    // The backend answers request-code with an unconditional 202 so nothing here
    // can learn whether an address already has an account — the UI copy has to
    // stay just as non-committal ("if that address can receive mail").
    const [emailStep, setEmailStep] = useState<'idle' | 'code'>('idle');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [emailBusy, setEmailBusy] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [emailNotice, setEmailNotice] = useState('');

    if (user && accountId) {
        return <Navigate to="/" />;
    }

    const requestEmailCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setEmailBusy(true);
        setEmailError('');
        try {
            await fetch('/api/auth/email/request-code', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ email }),
            });
            // 202 regardless of outcome — advance on transport success only, and
            // word it so a non-delivery reads as possible rather than impossible.
            setEmailStep('code');
            setEmailNotice(t('auth.login.codeSent', { email }));
        } catch {
            setEmailError(t('auth.login.errUnreachable'));
        } finally {
            setEmailBusy(false);
        }
    };

    const submitEmailCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setEmailBusy(true);
        setEmailError('');
        try {
            const res = await fetch('/api/auth/email/verify-code', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ email, code }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data?.ok) {
                setEmailError(data?.error || t('auth.login.errBadCode'));
                return;
            }
            // verify-code establishes the SAME passport session as the Google callback.
            await refresh();
            // SIGNUP + an email that already has accounts (next is NOT /onboarding) → show the
            // choice screen instead of silently logging in. LOGIN, or a brand-new email, follows
            // `next` (already the shared count-based destination: /onboarding, /, or /lobby).
            if (isSignup && data.next && data.next !== '/onboarding') {
                navigate('/welcome-back');
                return;
            }
            window.location.href = data.next || '/';
        } catch {
            setEmailError(t('auth.login.errUnreachable'));
        } finally {
            setEmailBusy(false);
        }
    };

    return (
        <div className="relative flex min-h-screen w-full flex-row font-sans bg-background-light dark:bg-background-dark text-text-strong dark:text-white overflow-x-hidden antialiased">
            {/* Left Side: Visual/Branding (Hidden on mobile) */}
            <div className="hidden lg:flex lg:w-1/2 relative flex-col items-center justify-center overflow-hidden bg-canvas dark:bg-[#1a2632]">
                <div className="absolute inset-0 z-0">
                    <img
                        alt="Abstract background"
                        className="h-full w-full object-cover opacity-90 dark:opacity-40"
                        src="https://lh3.googleusercontent.com/aida-public/AB6AXuC6VQV6_X7QbrzyumtqDAi_tm5bvV0rUopw-aMNrWEbGsHerHf5tKL1oLZbEyPSPS08Dz0wS581m7I3Gm7fdQFzxy9uwAke1V-bUx73ef-_5qANY5dF_o53gR271lHJaYlQ-Mlsh-xwajy9bQZ94nUbEGZ0fkpbJXIWVR8NWWdNzOuM137rk_FcwqEfOFePiY5wJeFYACZk6YPogSFOzjU4_xHbfpFwitBskASEsPsvyFHax0aZxQVshMW0y2BY8CRxXpDgn_WpUqkZ"
                    />
                    <div className="absolute inset-0 bg-primary/10 mix-blend-overlay"></div>
                </div>
                <div className="relative z-10 p-12 text-center max-w-lg">
                    <h2 className="text-3xl font-bold tracking-tight text-white drop-shadow-md mb-4">{t('auth.login.heroTitle')}</h2>
                    <p className="text-lg text-white/90 drop-shadow-md font-medium">{t('auth.login.heroSub')}</p>
                </div>
            </div>

            {/* Right Side: Login Form */}
            <div className="w-full lg:w-1/2 flex flex-col justify-center items-center p-6 md:p-12 lg:p-24 overflow-y-auto bg-background-light dark:bg-background-dark">
                <div className="w-full max-w-[480px] flex flex-col gap-6">
                    {/* Header / Logo */}
                    <div className="flex items-center gap-3 mb-2">
                        <div className="size-10 text-primary">
                            <svg className="w-full h-full" fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                                <path d="M13.8261 17.4264C16.7203 18.1174 20.2244 18.5217 24 18.5217C27.7756 18.5217 31.2797 18.1174 34.1739 17.4264C36.9144 16.7722 39.9967 15.2331 41.3563 14.1648L24.8486 40.6391C24.4571 41.267 23.5429 41.267 23.1514 40.6391L6.64374 14.1648C8.00331 15.2331 11.0856 16.7722 13.8261 17.4264Z" fill="currentColor"></path>
                                <path clipRule="evenodd" d="M39.998 12.236C39.9944 12.2537 39.9875 12.2845 39.9748 12.3294C39.9436 12.4399 39.8949 12.5741 39.8346 12.7175C39.8168 12.7597 39.7989 12.8007 39.7813 12.8398C38.5103 13.7113 35.9788 14.9393 33.7095 15.4811C30.9875 16.131 27.6413 16.5217 24 16.5217C20.3587 16.5217 17.0125 16.131 14.2905 15.4811C12.0012 14.9346 9.44505 13.6897 8.18538 12.8168C8.17384 12.7925 8.16216 12.767 8.15052 12.7408C8.09919 12.6249 8.05721 12.5114 8.02977 12.411C8.00356 12.3152 8.00039 12.2667 8.00004 12.2612C8.00004 12.261 8 12.2607 8.00004 12.2612C8.00004 12.2359 8.0104 11.9233 8.68485 11.3686C9.34546 10.8254 10.4222 10.2469 11.9291 9.72276C14.9242 8.68098 19.1919 8 24 8C28.8081 8 33.0758 8.68098 36.0709 9.72276C37.5778 10.2469 38.6545 10.8254 39.3151 11.3686C39.9006 11.8501 39.9857 12.1489 39.998 12.236ZM4.95178 15.2312L21.4543 41.6973C22.6288 43.5809 25.3712 43.5809 26.5457 41.6973L43.0534 15.223C43.0709 15.1948 43.0878 15.1662 43.104 15.1371L41.3563 14.1648C43.104 15.1371 43.1038 15.1374 43.104 15.1371L43.1051 15.135L43.1065 15.1325L43.1101 15.1261L43.1199 15.1082C43.1276 15.094 43.1377 15.0754 43.1497 15.0527C43.1738 15.0075 43.2062 14.9455 43.244 14.8701C43.319 14.7208 43.4196 14.511 43.5217 14.2683C43.6901 13.8679 44 13.0689 44 12.2609C44 10.5573 43.003 9.22254 41.8558 8.2791C40.6947 7.32427 39.1354 6.55361 37.385 5.94477C33.8654 4.72057 29.133 4 24 4C18.867 4 14.1346 4.72057 10.615 5.94478C8.86463 6.55361 7.30529 7.32428 6.14419 8.27911C4.99695 9.22255 3.99999 10.5573 3.99999 12.2609C3.99999 13.1275 4.29264 13.9078 4.49321 14.3607C4.60375 14.6102 4.71348 14.8196 4.79687 14.9689C4.83898 15.0444 4.87547 15.1065 4.9035 15.1529C4.91754 15.1762 4.92954 15.1957 4.93916 15.2111L4.94662 15.223L4.95178 15.2312ZM35.9868 18.996L24 38.22L12.0131 18.996C12.4661 19.1391 12.9179 19.2658 13.3617 19.3718C16.4281 20.1039 20.0901 20.5217 24 20.5217C27.9099 20.5217 31.5719 20.1039 34.6383 19.3718C35.082 19.2658 35.5339 19.1391 35.9868 18.996Z" fill="currentColor" fillRule="evenodd"></path>
                            </svg>
                        </div>
                        <span className="text-xl font-bold tracking-tight dark:text-white">Agent Console</span>
                    </div>

                    {/* Text Content — framing follows the mode */}
                    <div className="space-y-2">
                        <h1 className="text-text-strong dark:text-white tracking-tight text-[32px] font-bold leading-tight">
                            {isSignup ? t('auth.login.titleSignup') : t('auth.login.titleSignin')}
                        </h1>
                        <p className="text-text-muted dark:text-slate-400 text-base font-normal leading-normal">
                            {isSignup
                                ? t('auth.login.subSignup')
                                : t('auth.login.subSignin')}
                        </p>
                    </div>

                    {/* Log in / Sign up toggle */}
                    <div className="flex items-center gap-1 rounded-lg bg-canvas dark:bg-[#1a2632] p-1 text-sm font-medium">
                        <button
                            type="button"
                            onClick={() => setMode('login')}
                            className={`flex-1 rounded-md py-2 transition-colors ${!isSignup ? 'bg-white dark:bg-[#1e2b37] text-primary shadow-sm' : 'text-text-muted'}`}
                        >
                            {t('auth.login.tabLogin')}
                        </button>
                        <button
                            type="button"
                            onClick={() => setMode('signup')}
                            className={`flex-1 rounded-md py-2 transition-colors ${isSignup ? 'bg-white dark:bg-[#1e2b37] text-primary shadow-sm' : 'text-text-muted'}`}
                        >
                            {t('auth.login.tabSignup')}
                        </button>
                    </div>

                    {/* Google Button — carries signup-vs-login intent through OAuth via ?intent= (state) */}
                    <button
                        onClick={() => { window.location.href = `/auth/google?intent=${mode}`; }}
                        className="flex w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-4 bg-primary hover:bg-primary/90 text-white text-base font-bold leading-normal tracking-[0.015em] transition-colors shadow-sm mt-4 gap-3"
                    >
                        <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5 bg-white rounded-full p-0.5" />
                        <span className="truncate">{isSignup ? t('auth.login.googleSignup') : t('auth.login.googleSignin')}</span>
                    </button>

                    {/* Passwordless email — the second front door, peer of Google above */}
                    <div className="flex items-center gap-3" aria-hidden="true">
                        <div className="h-px flex-1 bg-border-neutral dark:bg-[#31465c]" />
                        <span className="text-xs font-medium uppercase tracking-wider text-text-muted dark:text-slate-400">or</span>
                        <div className="h-px flex-1 bg-border-neutral dark:bg-[#31465c]" />
                    </div>

                    {emailStep === 'idle' ? (
                        <form onSubmit={requestEmailCode} className="flex flex-col gap-3">
                            <label htmlFor="email-signin" className="text-[11px] font-bold uppercase tracking-wider text-text-muted">
                                {t('auth.login.continueWithEmail')}
                            </label>
                            <div className="relative">
                                <AtSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                                <input
                                    id="email-signin"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder={t('auth.login.emailPlaceholder')}
                                    autoComplete="email"
                                    className="h-12 w-full rounded-lg border border-border-neutral dark:border-[#31465c] bg-white dark:bg-[#1e2b37] pl-10 pr-3 text-base text-black dark:text-white focus:border-primary focus:outline-none"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={emailBusy}
                                className="flex h-12 w-full cursor-pointer items-center justify-center gap-3 rounded-lg border border-primary/40 bg-primary/5 px-4 text-base font-bold tracking-[0.015em] text-primary transition-colors hover:bg-primary/10 disabled:opacity-50"
                            >
                                <Mail size={18} />
                                <span className="truncate">{emailBusy ? t('auth.login.sendCodeBusy') : t('auth.login.sendCode')}</span>
                            </button>
                            <p className="text-xs text-text-muted dark:text-slate-400">{t('auth.login.noPassword')}</p>
                        </form>
                    ) : (
                        <form onSubmit={submitEmailCode} className="flex flex-col gap-3">
                            <label htmlFor="email-code" className="text-[11px] font-bold uppercase tracking-wider text-text-muted">
                                {t('auth.login.enterCode')}
                            </label>
                            {emailNotice && <p className="text-xs text-text-muted dark:text-slate-400">{emailNotice}</p>}
                            <input
                                id="email-code"
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                pattern="[0-9]*"
                                maxLength={6}
                                required
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                                placeholder="123456"
                                className="h-12 w-full rounded-lg border border-border-neutral dark:border-[#31465c] bg-white dark:bg-[#1e2b37] px-3 text-center text-2xl font-bold tracking-[0.5em] text-black dark:text-white focus:border-primary focus:outline-none"
                            />
                            {emailError && <p className="text-xs font-medium text-red-600 dark:text-red-400">{emailError}</p>}
                            <button
                                type="submit"
                                disabled={emailBusy || code.length !== 6}
                                className="flex h-12 w-full cursor-pointer items-center justify-center rounded-lg bg-primary px-4 text-base font-bold tracking-[0.015em] text-white shadow-sm transition-colors hover:bg-primary/90 disabled:opacity-50"
                            >
                                {emailBusy ? t('auth.login.verifyBusy') : t('auth.login.verify')}
                            </button>
                            <button
                                type="button"
                                onClick={() => { setEmailStep('idle'); setCode(''); setEmailError(''); setEmailNotice(''); }}
                                className="text-xs font-medium text-text-muted underline-offset-2 hover:underline dark:text-slate-400"
                            >
                                {t('auth.login.useDifferentEmail')}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
